import { ahuBoardApi } from 'app/services/ahuBoardApi';

/**
 * Phase 6e2c-4 — FILTER selector data layer (§33.7).
 *
 * The filter selector operates on the LIVE filter form model (via
 * window.__FORM_SHIM__[componentId] — the Q15 shim), NOT a staging clone. The
 * legacy view reaches into options.current_model.view (the filter form):
 *   - shim #2  view.model           — the live Backbone filter model
 *   - shim #3  view.rate (get/set)  — suppresses §49.2 saves while rating owns it
 *   - shim #5  catalogueFields[t].cmsPages.cms_count / cms_full_count — counts
 *   - shim #6  catalogueFields[t].resetFilter — write-back selection filters
 *   - shim #8  catalogueFields.master_filter_id.catalogueSelector.{...} — the
 *              embedded smart-list DOM surgery (relocate into dialog + restore)
 *
 * This file adds ONLY the HTTP the selector itself owns (the form's catalogue
 * counts/pages already flow through ahuComponentFormsApi + catalogueField.ts):
 *
 *  READ-ONLY (fire on open / browse — never mutate):
 *   - getFilterSizesAndQuantities — master/v1.0.0/ahu/filter_sizes_and_quantities/
 *     ?page&cms_id&max_different_sizes&arr_id&mounting_angle&… (+ ?id= detail)
 *   - getFilterLoadings           — cms/1/v1.0.0/ahu/filters/loadings/?<deps>
 *   - getFilterFrameParams        — cms/1/v1.0.0/ahu/filters/frames/<name>/?<deps>
 *   - getFilterSizes              — master/v1.0.0/ahu/filter_sizes/ (allowable-sizes UI)
 *   - getFilterSelectionCondition — cms/1/v1.0.0/ahu/filters/selection_condition/
 *
 *  MUTATING (★ writes shared dev3 — userArmed-gated, CODE-ONLY in smoke):
 *   - patchFilterRate (getFilterData round-trip): PATCH ahu/filters/<id>
 *     {filter_params, frame_params, master_filter_id, master_frame_id,
 *      arrangement_id, airflow_actual, center_high, final_recommended_apd?,
 *      size_qty_id, rate:true, frame_loading_id, height_from_floor?,
 *      mounting_angle, distance_from_section_ups} → server computes the bank
 *     outputs (qty_*, initial_apd, face_velocity_actual, height, …). The legacy
 *     PATCH PERSISTS while previewing (unlike accessory staging) — §33.7 NB.
 *     Gated behind the selector's userArmed ref (no save-on-open — same rule as
 *     the 6e2a init gate); Select also writes (see selectFilter in FilterSelector).
 *
 * The Select APPLY (selectFilter) writes through the shim's FormModel
 * (current_model.save(diff,{patch:true})) — NOT a mutation here — so it reuses
 * the §49.2 auto-save PATCH pipeline (ahuBoardFormsApi.patchConcreteRow). Only
 * the rate-preview round-trip needs its own mutation (legacy fn.uni_tools.patch
 * directly, bypassing the form model's save gate).
 */

/** Loose bank-config row (filter_sizes_and_quantities item). */
export interface IFilterSizeQtyRow {
  id: number;
  wide_high: string;
  bank_height: number;
  bank_width: number;
  [k: string]: unknown;
}

/** Loose filter-size row (allowable-sizes multiselect). */
export interface IFilterSizeRow {
  id: number;
  name: string;
  value: string;
  width: number;
  height: number;
  [k: string]: unknown;
}

/** The computed-outputs PATCH response (getFilterData). */
export type IFilterRateResult = Record<string, unknown>;

export const filterSelectorApi = ahuBoardApi.injectEndpoints({
  endpoints: (builder) => ({
    // ---- READ-ONLY: bank configurations (filter_sizes_and_quantities) -------
    // url built by the caller verbatim (legacy getFilterSizesAndQuantitiesUrl):
    // both the list form (?page&cms_id&max_different_sizes&arr_id&mounting_angle
    // [+frame/frame_loading_id/height_from_floor/allowable_filter_sizes/
    // min_max_velocity/airflow/selection_condition/airway_*]) and the by-id
    // detail form (?page=1&id=<size_qty_id>). Returns an array.
    getFilterSizesAndQuantities: builder.query<IFilterSizeQtyRow[], string>({
      query: (url) => url,
      transformResponse: (res: unknown): IFilterSizeQtyRow[] =>
        Array.isArray(res) ? (res as IFilterSizeQtyRow[]) : [],
      keepUnusedDataFor: 300,
    }),

    // ---- READ-ONLY: frame loadings (cms/.../filters/loadings/?<deps>) -------
    getFilterLoadings: builder.query<Array<Record<string, unknown>>, string>({
      query: (url) => url,
      transformResponse: (res: unknown): Array<Record<string, unknown>> =>
        Array.isArray(res) ? (res as Array<Record<string, unknown>>) : [],
      keepUnusedDataFor: 24 * 60 * 60,
    }),

    // ---- READ-ONLY: frame param lists (cms/.../filters/frames/<name>/) ------
    // legacy loadParams returns list[name] || list. The caller passes the full
    // URL (with the <name> segment + dependency query); we hand back the body.
    getFilterFrameParams: builder.query<unknown, string>({
      query: (url) => url,
      keepUnusedDataFor: 24 * 60 * 60,
    }),

    // ---- READ-ONLY: filter sizes catalogue (allowable-sizes multiselect) ----
    getFilterSizes: builder.query<IFilterSizeRow[], void>({
      query: () => '/api/v1.0/master/v1.0.0/ahu/filter_sizes/',
      transformResponse: (res: unknown): IFilterSizeRow[] => {
        if (Array.isArray(res)) return res as IFilterSizeRow[];
        // legacy fn['current.filter.sizes'] is a keyed dict — Object.values.
        if (res && typeof res === 'object')
          return Object.values(res as Record<string, IFilterSizeRow>);
        return [];
      },
      keepUnusedDataFor: 24 * 60 * 60,
    }),

    // ---- READ-ONLY: selection_condition collection -------------------------
    // legacy model/filter.selection.condition.js urlRoot.
    getFilterSelectionCondition: builder.query<
      Array<Record<string, unknown>>,
      void
    >({
      query: () => '/api/v1.0/master/v1.0.0/ahu/selection_conditions/',
      transformResponse: (res: unknown): Array<Record<string, unknown>> => {
        if (Array.isArray(res)) return res as Array<Record<string, unknown>>;
        if (res && typeof res === 'object')
          return Object.values(res as Record<string, Record<string, unknown>>);
        return [];
      },
      keepUnusedDataFor: 24 * 60 * 60,
    }),

    // ---- READ-ONLY: pricing methods (legacy stateStorage 'pricing.methods') -
    // legacy collection/pricing_methods.js urlRoot (master, not cms).
    getFilterPricingMethods: builder.query<Array<Record<string, unknown>>, void>({
      query: () => '/api/v1.0/master/v1.0.0/ahu/pricing_methods/',
      transformResponse: (res: unknown): Array<Record<string, unknown>> =>
        Array.isArray(res) ? (res as Array<Record<string, unknown>>) : [],
      keepUnusedDataFor: 24 * 60 * 60,
    }),

    // =======================================================================
    // ★ MUTATING — getFilterData preview round-trip (§33.7).
    //   PERSISTS to shared dev3. userArmed-gated by the selector; CODE-ONLY in
    //   smoke. Body shape is byte-faithful to filter.selector.js getFilterData.
    // =======================================================================
    patchFilterRate: builder.mutation<
      IFilterRateResult,
      { id: number; data: Record<string, unknown> }
    >({
      query: ({ id, data }) => ({
        // legacy fn.uni_tools.patch('/api/v1.0/ahu/filters/<id>', {...}) — the
        // DRF detail route is slashless (same as getComponentDetail GETs).
        url: `/api/v1.0/ahu/filters/${id}`,
        method: 'PATCH',
        body: data,
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetFilterSizesAndQuantitiesQuery,
  useLazyGetFilterSizesAndQuantitiesQuery,
  useGetFilterLoadingsQuery,
  useGetFilterFrameParamsQuery,
  useGetFilterSizesQuery,
  useGetFilterSelectionConditionQuery,
  useGetFilterPricingMethodsQuery,
  usePatchFilterRateMutation,
} = filterSelectorApi;
